import React, { useState, useEffect, memo } from 'react';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { Container, Card } from 'react-bootstrap';
import { useSpring, animated } from 'react-spring';
import Nav from '../components/nav';
import '../styles/mainSection.css';
import styled from 'styled-components';
import { Sparkles, Users, MessageCircle, Globe } from 'lucide-react';
import { baseUrl } from '../config';
//imports of images on landing page 
// import RRRImage from '../assets/img/RRR.jpg';
import CommunityImage from '../assets/img/community.gif'; 
import BlogImage from '../assets/img/recent_blogs.png'; 
import Filmanagement from '../assets/img/Casting-assistant.png';
import CA from  '../assets/img/Casting and audition.png';
import Distribution_Network from '../assets/img/Location.png';
import PostProductionSuite from '../assets/img/Post.png';
import marketing from '../assets/img/marketing assistant.png';
import example1 from '../assets/img/p1.png';
import example2 from '../assets/img/p2.gif';
import heroBg from '../assets/img/herosectionbg.png';
// Note: the requested filename was "advertisementbg.png" but the actual
// file on disk is spelled "advertismentbg.png" (missing the second "e").
// Importing the real file — see report at the end of this task.
import ctaBg from '../assets/img/advertismentbg.png';
import studentProgramBanner1 from '../assets/img/student-program1.jpg';
//external imports
import AdBanner from '../components/AdBanner';
import MainDesign from '../components/Maindesign';
import ParllelSections from '../components/Parllelscroll';

// ── FIX 1: Static arrays moved OUTSIDE component ──────────────────────────────
// (Inside the component they were recreated every render → stale closure in useEffect)
const defaultFeaturedProjects = [
  {
    id: 1,
    title: 'Promote Your Project',
    description: 'Showcase your film projects to a global audience and gain the visibility they deserve.',
    img: example1,
    url: "#",
  },
  {
    id: 2,
    title: 'Showcase Your Talent',
    description: 'Highlight your unique skills and talents to connect with industry professionals.',
    img: example2,
    url: "#",
  },
  {
    id: 3,
    title: 'Expand Your Reach',
    description: 'Leverage our platform to reach new audiences and grow your fanbase.',
    img: 'https://via.placeholder.com/150',
    url: "#",
  },
  {
    id: 4,
    title: 'Collaborate with Creators',
    description: 'Find like-minded creators and collaborate on exciting new projects.',
    img: 'https://via.placeholder.com/150',
    url: "#",
  },
  {
    id: 5,
    title: 'Discover Opportunities',
    description: 'Explore opportunities to take your career in the film industry to the next level.',
    img: 'https://via.placeholder.com/150',
    url: "#",
  },
];

const defaultServicesOffered = [
  {
    id: 1,
    title: 'Film Production Management',
    description: 'Efficiently manage film production processes with our comprehensive solution.',
    img: Filmanagement,
    url: "/film-production-guide",
  },
  {
    id: 2,
    title: 'Casting and Audition Platform',
    description: 'Streamline casting and audition processes with our innovative platform.',
    img: CA,
    url: "/casting-guide",
  },
  {
    id: 3,
    title: 'Post-Production Suite',
    description: 'Optimize post-production workflows with our advanced suite of tools.',
    img: PostProductionSuite,
    url: "/post-production-guide",
  },
  {
    id: 4,
    title: 'Film Distribution Network',
    description: 'Expand the reach of your films with our global distribution network.',
    img: Distribution_Network,
    url: "/distribution-guide",
  },
  {
    id: 5,
    title: 'Film Marketing Analytics',
    description: 'Gain valuable insights into film marketing campaigns with our analytics solution.',
    img: marketing,
    url: "/digital",
  },
];

const featureHighlights = [
  {
    id: 1,
    icon: <Sparkles size={28} />,
    title: 'AI-Powered Planning',
    description: 'AI-driven script analysis, smart scheduling, and budget planning — all in one place.',
    iconBg: '#008080',
  },
  {
    id: 2,
    icon: <Users size={28} />,
    title: 'Find the Right People',
    description: "Connect with cast, crew, and collaborators who match your project's needs.",
    iconBg: '#7c3aed',
  },
  {
    id: 3,
    icon: <MessageCircle size={28} />,
    title: 'Collaborate Seamlessly',
    description: 'Keep every department in sync with real-time messaging and shared workspaces.',
    iconBg: '#f97316',
  },
  {
    id: 4,
    icon: <Globe size={28} />,
    title: 'Distribute Globally',
    description: 'Reach audiences everywhere with tools built for worldwide film distribution.',
    iconBg: '#ec4899',
  },
];

// ─────────────────────────────────────────────────────────────────────────────

const MacOSCard = memo(({ children, className = '', style }) => {
  return (
    <Card className={`custom-card shadow-sm h-100 ${className}`.trim()} style={style}>
      <div className="window-controls flex items-center space-x-2 p-2">
        {['close', 'minimize', 'maximize'].map((action) => (
          <div
            key={action}
            className={`w-3 h-3 rounded-full border border-dark ${
              action === 'close' ? 'bg-danger' :
              action === 'minimize' ? 'bg-warning' : 'bg-success'
            }`}
            aria-label={`${action} window`}
          />
        ))}
      </div>
      {children}
    </Card>
  );
});

// Center-focused ("coverflow") carousel for the Featured Projects
// section — one enlarged, fully-opaque card in the middle, adjacent
// cards peeking in from the edges at reduced scale/opacity.
const FP_OFFSETS = [-2, -1, 0, 1, 2];

const FP_POSITION_STYLES = {
  0: { translateX: 0, scale: 1, zIndex: 50, opacity: 1, boxShadow: '0 20px 40px rgba(0,0,0,0.25)' },
  1: { translateX: 250, scale: 0.85, zIndex: 30, opacity: 1, boxShadow: '0 8px 20px rgba(0,0,0,0.12)' },
  2: { translateX: 450, scale: 0.7, zIndex: 10, opacity: 0.9, boxShadow: '0 4px 10px rgba(0,0,0,0.08)' },
};

// Generic center-focused ("depth") carousel — shared by both the
// Featured Projects and Services Offered sections. One enlarged,
// fully-opaque card in the middle, adjacent cards peeking in from the
// edges at reduced scale/opacity.
const DepthCarousel = ({ items, title }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const getItem = (offset) => items[(activeIndex + offset + items.length * 10) % items.length];

  const goLeft = () => setActiveIndex((i) => (i - 1 + items.length) % items.length);
  const goRight = () => setActiveIndex((i) => (i + 1) % items.length);

  // Show at most 5 slots (the coverflow's designed spread), but never
  // more than there are distinct items. A contiguous run of N offsets
  // is always injective mod items.length when N <= items.length, so
  // this can never resolve two slots to the same item - unlike the old
  // fixed 5-offset array, which collided whenever items.length < 5.
  const visibleCount = Math.min(items.length, FP_OFFSETS.length);
  const start = -Math.ceil((visibleCount - 1) / 2);
  const offsets = Array.from({ length: visibleCount }, (_, i) => start + i);

  return (
    <div className="featured-projects-section" role="region" aria-label={`${title} carousel`}>
      <h2 className="featured-projects-title">{title}</h2>
      <div className="featured-projects-carousel-wrapper">
        <button
          type="button"
          className="fp-nav-arrow fp-nav-arrow-left"
          aria-label="Previous item"
          onClick={goLeft}
        >
          <FaArrowLeft />
        </button>

        <div className="fp-carousel-track">
          {offsets.map((offset) => {
            const item = getItem(offset);
            const pos = FP_POSITION_STYLES[Math.abs(offset)];
            const translateX = offset < 0 ? -pos.translateX : pos.translateX;
            return (
              <a
                href={item.url}
                key={item.id}
                className="fp-card-link fp-card-positioned"
                style={{
                  transform: `translateX(${translateX}px) scale(${pos.scale})`,
                  zIndex: pos.zIndex,
                  opacity: pos.opacity,
                  pointerEvents: offset === 0 ? 'auto' : 'none',
                }}
              >
                <MacOSCard className="fp-card" style={{ boxShadow: pos.boxShadow }}>
                  <Card.Img variant="top" src={item.img} alt={item.title} className="fp-card-img" loading="lazy" />
                  <Card.Body className="fp-card-body">
                    <Card.Title className="fp-card-title"><span>{item.title}</span></Card.Title>
                    <Card.Text className="fp-card-text"><span>{item.description}</span></Card.Text>
                    <span className="fp-view-details">
                      View Details <span aria-hidden="true">→</span>
                    </span>
                  </Card.Body>
                </MacOSCard>
              </a>
            );
          })}
        </div>

        <button
          type="button"
          className="fp-nav-arrow fp-nav-arrow-right"
          aria-label="Next item"
          onClick={goRight}
        >
          <FaArrowRight />
        </button>
      </div>
    </div>
  );
};

const CurvedWelcomeSection = styled(motion.div)`
  position: relative;
  background-image: linear-gradient(to right, #008080, #20B2AA);
  color: white;
  padding: 6rem 0;
  min-height: 640px;
  overflow: hidden;
  text-align: left;
  display: flex;
  align-items: center;

  .hero-bg-image {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: 60% 35%;
    z-index: 0;
    /* Hidden until (and unless) the placeholder path below actually
       resolves to a real image - the teal gradient above stays the
       visible fallback. onError also hides it as a second safety net. */
    opacity: 0;
    transition: opacity 0.4s ease;
  }

  .hero-bg-image.loaded {
    opacity: 1;
  }

  .hero-curve-svg {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 212px;
    z-index: 0;
    display: block;
  }

  .welcome-content {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 600px;
    margin: 0;
    padding: 0 20px 0 60px;
  }

  h1 {
    font-size: 4.5rem;
    margin-bottom: 1.5rem;
    font-weight: 700;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
  }

  p {
    font-size: 1.25rem;
    margin-bottom: 2.5rem;
    line-height: 1.6;
  }

  .buttons-container {
    display: flex;
    gap: 20px;
    justify-content: flex-start;
    align-items: center;
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
    min-height: 560px;

    .welcome-content {
      max-width: 100%;
      padding: 0 24px;
    }

    h1 {
      font-size: 2.75rem;
    }

    p {
      font-size: 1.1rem;
    }

    .buttons-container {
      flex-direction: column;
      align-items: flex-start;
      gap: 15px;
    }

    .hero-curve-svg {
      height: 71px;
    }
  }
`;

const StyledButton = styled(animated.button)`
  padding: 0.875rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  transition: transform 0.2s ease, background-color 0.2s ease;
  width: fit-content;
  flex-shrink: 0;
  align-self: flex-start;

  &.primary {
    border: none;
    background: white;
    color: #008080;
  }

  &.secondary {
    border: 1.5px solid rgba(255, 255, 255, 0.8);
    background: transparent;
    color: white;
  }

  &.secondary:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: white;
  }
`;

const ShowcaseSection = styled.div`
  padding: 4rem 0;
  background: #f8f9fa;
  
  .showcase-content {
    display: flex;
    align-items: center;
    gap: 2rem;
    margin-bottom: 2rem;
  }

  .showcase-text {
    flex: 1;
    padding: 2rem;
  }

  .showcase-title {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    color: #333;
  }

  .showcase-description {
    font-size: 1.2rem;
    line-height: 1.6;
    color: #666;
    margin-bottom: 2rem;
  }

  .showcase-image-container {
    flex: 1;
    max-width: 500px;
  }

  @media (max-width: 768px) {
    .showcase-content {
      flex-direction: column;
      text-align: center;
    }

    .showcase-image-container {
      order: -1;
      margin-bottom: 2rem;
    }
  }
`;

const ShowcaseButton = styled(motion.button)`
  padding: 0.875rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 50px;
  border: none;
  cursor: pointer;
  background: #008080;
  color: white;
  transition: all 0.3s ease;

  &:hover {
    background: #006666;
    transform: translateY(-2px);
  }
`;

const CTASection = styled.section`
  position: relative;
  min-height: 340px;
  margin: 0 16px 70px;
  padding: 4rem 1.5rem 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background-image: url(${(props) => props.$bgImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  color: white;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.35);
  }

  .cta-content {
    position: relative;
    z-index: 1;
    max-width: 700px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  h2 {
    font-size: 2.75rem;
    font-weight: 700;
    margin-bottom: 1rem;
    text-shadow: 2px 2px 6px rgba(0, 0, 0, 0.3);
  }

  p {
    font-size: 1.2rem;
    margin-bottom: 2rem;
    line-height: 1.6;
  }

  @media (max-width: 768px) {
    min-height: 260px;
    margin: 0 10px 40px;
    padding: 2.75rem 1.25rem 1.75rem;

    h2 {
      font-size: 2rem;
    }

    p {
      font-size: 1.05rem;
    }
  }
`;

const FeatureGridSection = styled.section`
  padding: 4rem 0 1rem;
  background: #f8f9fa;

  .feature-grid-inner {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  .feature-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 2rem;
  }

  .feature-card {
    background: white;
    border-radius: 15px;
    padding: 3rem 2rem;
    min-height: 280px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }

  .feature-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
  }

  .feature-icon-circle {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 0 1.5rem;
    flex-shrink: 0;
  }

  .feature-card h3 {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 0.75rem;
  }

  .feature-card p {
    color: #7f8c8d;
    font-size: 0.95rem;
    line-height: 1.5;
    margin: 0;
  }

  @media (max-width: 992px) {
    .feature-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (max-width: 576px) {
    padding: 3rem 0 0.5rem;

    .feature-grid {
      grid-template-columns: 1fr;
    }
  }
`;

const HERO_BG_IMAGE_SRC = heroBg;

export function MainSection() {
  const [isPrimaryHovered, setPrimaryHovered] = useState(false);
  const [heroBgLoaded, setHeroBgLoaded] = useState(false);
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [servicesOffered, setServicesOffered] = useState([]);

  useEffect(() => {
    fetch(`${baseUrl}/featured_projects`)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.json();
      })
      .then((data) => {
        setFeaturedProjects(data);
      })
      .catch((error) => {
        setFeaturedProjects(defaultFeaturedProjects);
      });
  }, []);

  useEffect(() => {
    fetch(`${baseUrl}/services_offered`)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.json();
      })
      .then((data) => {
        setServicesOffered(data);
      })
      .catch((error) => {
        setServicesOffered(defaultServicesOffered);
      });
  }, []);

  const primarySpringProps = useSpring({
    transform: isPrimaryHovered ? 'scale(1.05)' : 'scale(1)',
    boxShadow: isPrimaryHovered 
      ? '0 10px 20px rgba(0,0,0,0.2)' 
      : '0 5px 10px rgba(0,0,0,0.1)',
  });

  return (
    // ── FIX 3: suppressHydrationWarning → stops browser extensions causing removeChild crash ──
    <div className="main" suppressHydrationWarning={true}>
      <Nav />
      <Container fluid className="px-0">
        <CurvedWelcomeSection
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          {/* Hero background photo. Teal gradient above stays as a
              fallback (visible until this loads, and again if it ever
              fails to load) via the opacity/onError logic below. */}
          <img
            className={`hero-bg-image${heroBgLoaded ? ' loaded' : ''}`}
            src={HERO_BG_IMAGE_SRC}
            alt=""
            aria-hidden="true"
            loading="lazy"
            onLoad={() => setHeroBgLoaded(true)}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          {/* Bottom curve divider - an actual SVG (not clip-path), so
              viewBox + preserveAspectRatio="none" stretches the single
              path to fill the section's real rendered width at any
              viewport size, instead of a clip-path's fixed-px coordinates
              that only line up at one reference width. */}
          <svg
            className="hero-curve-svg"
            viewBox="0 0 1200 240"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path d="M0,240 Q300,0 600,120 T1200,72 L1200,240 L0,240 Z" fill="white" />
          </svg>
          <div className="welcome-content">
            <h1>Welcome to ProducerBuddy</h1>
            <p>
              A product of Pilomation Company, revolutionizing the film industry
              from pre-production to post-production.
            </p>
            <div className="buttons-container">
              <StyledButton
                className="primary"
                style={primarySpringProps}
                onMouseEnter={() => setPrimaryHovered(true)}
                onMouseLeave={() => setPrimaryHovered(false)}
                onClick={() => window.location.href = '/howto'}
              >
                Explore ProducerBuddy
              </StyledButton>
              <StyledButton
                className="secondary"
                onClick={() => window.location.href = 'https://www.pilomation.com/producerbuddy/book-demo'}
              >
                Book Demo
              </StyledButton>
            </div>
          </div>
        </CurvedWelcomeSection>

        <FeatureGridSection>
          <div className="feature-grid-inner">
            <div className="feature-grid">
              {featureHighlights.map((feature) => (
                <div className="feature-card" key={feature.id}>
                  <div className="feature-icon-circle" style={{ background: feature.iconBg }}>
                    {feature.icon}
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </FeatureGridSection>

        <div className="myy-5">
          <div className="main_section">
            <DepthCarousel
              items={featuredProjects.length > 0 ? featuredProjects : defaultFeaturedProjects}
              title="Featured Projects"
            />
            <div onClick={() => window.location.href = '/student-program'} className="cursor-pointer w-full">
              <img src={studentProgramBanner1} alt="Student Program" className="w-full h-auto" />
            </div>
            <MainDesign />
            <DepthCarousel
              items={servicesOffered.length > 0 ? servicesOffered : defaultServicesOffered}
              title="Our Services"
            />
            <AdBanner />
            {/* Community Showcase Section */}
            <ShowcaseSection>
              <div className="showcase-content">
                <div className="showcase-text">   
                  <h2 className="showcase-title">Global Film Community</h2>
                  <p className="showcase-description">
                    Join our vibrant community of filmmakers, producers, and industry professionals. 
                    Share experiences, collaborate on projects, and stay connected with the latest 
                    industry trends. Our platform provides a space for meaningful discussions, 
                    resource sharing, and networking opportunities that can help take your 
                    productions to the next level.
                  </p>
                  <ShowcaseButton
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => window.location.href = '/comunity'}
                  >
                    Join Now
                  </ShowcaseButton>
                </div>
                <div className="showcase-image-container">
                  <MacOSCard>
                    <Card.Img 
                      variant="top" 
                      src={CommunityImage} 
                      alt="Film Community" 
                      style={{ 
                        height: '600px',
                        objectFit: 'cover',
                        borderRadius: '0 0 10px 10px'
                      }}
                      loading="lazy"
                    />
                  </MacOSCard>
                </div>
              </div>
            </ShowcaseSection>
            <ParllelSections />

            {/* Blog Showcase Section */}
            <ShowcaseSection>
              <div className="showcase-content">
                <div className="showcase-image-container">
                  <MacOSCard>
                    <Card.Img 
                      variant="top" 
                      src={BlogImage} 
                      alt="Blog Posts" 
                      style={{ 
                        height: '400px',
                        objectFit: 'cover',
                        borderRadius: '0 0 10px 10px'
                      }}
                      loading="lazy"
                    />
                  </MacOSCard>
                </div>
                <div className="showcase-text">
                  <h2 className="showcase-title">Explore Recent Blogs</h2>
                  <p className="showcase-description">
                    Dive into our collection of insightful articles, production tips, and industry 
                    analyses written by experienced professionals. Stay informed about the latest 
                    trends, technologies, and best practices in film production. Whether you're a 
                    seasoned producer or just starting out, our blog provides valuable knowledge 
                    to enhance your filmmaking journey.
                  </p>
                  <ShowcaseButton
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => window.location.href = '/blog'}
                  >
                    Write Your Thoughts
                  </ShowcaseButton>
                </div>
              </div>
            </ShowcaseSection>

            <CTASection $bgImage={ctaBg}>
              <div className="cta-content">
                <h2>Ready to Elevate Your Film Journey?</h2>
                <p>Join ProducerBuddy today and be part of the future of cinema</p>
                <ShowcaseButton
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => window.location.href = '/signup'}
                >
                  Sign Up for Free
                </ShowcaseButton>
              </div>
            </CTASection>
          </div>
        </div>
      </Container>
    </div>
  );
}
